import { Rng } from '../rng';
import { dist } from '../math';

export const TILE = 32;

export interface Point {
  x: number;
  y: number;
}

/**
 * Tile map with solid flags and difficulty tiers. Realm maps are square with the boss
 * arena in the centre; tiers grow toward the centre.
 */
export class GameMap {
  readonly solid: Uint8Array;
  readonly tiers: Uint8Array;
  readonly spawnPoints: Point[] = [];
  readonly center: Point;
  readonly widthPx: number;
  readonly heightPx: number;

  constructor(
    readonly w: number,
    readonly h: number,
    readonly arenaRadius: number,
    readonly kind: 'realm' | 'arena',
    readonly seed: number,
  ) {
    this.solid = new Uint8Array(w * h);
    this.tiers = new Uint8Array(w * h);
    this.widthPx = w * TILE;
    this.heightPx = h * TILE;
    this.center = { x: this.widthPx / 2, y: this.heightPx / 2 };
  }

  idx(tx: number, ty: number): number {
    return ty * this.w + tx;
  }

  inBounds(tx: number, ty: number): boolean {
    return tx >= 0 && ty >= 0 && tx < this.w && ty < this.h;
  }

  isSolidTile(tx: number, ty: number): boolean {
    if (!this.inBounds(tx, ty)) return true;
    return this.solid[this.idx(tx, ty)] === 1;
  }

  isSolidAt(x: number, y: number): boolean {
    return this.isSolidTile(Math.floor(x / TILE), Math.floor(y / TILE));
  }

  tierAt(x: number, y: number): number {
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    if (!this.inBounds(tx, ty)) return 1;
    return this.tiers[this.idx(tx, ty)] || 1;
  }

  /** Pushes a circle out of solid tiles. Returns the corrected position. */
  resolveCircle(x: number, y: number, r: number): Point {
    for (let pass = 0; pass < 2; pass++) {
      const tx0 = Math.floor((x - r) / TILE);
      const tx1 = Math.floor((x + r) / TILE);
      const ty0 = Math.floor((y - r) / TILE);
      const ty1 = Math.floor((y + r) / TILE);
      for (let ty = ty0; ty <= ty1; ty++) {
        for (let tx = tx0; tx <= tx1; tx++) {
          if (!this.isSolidTile(tx, ty)) continue;
          const left = tx * TILE;
          const top = ty * TILE;
          const cx = Math.max(left, Math.min(x, left + TILE));
          const cy = Math.max(top, Math.min(y, top + TILE));
          let dx = x - cx;
          let dy = y - cy;
          let d2 = dx * dx + dy * dy;
          if (d2 >= r * r) continue;
          if (d2 < 1e-6) {
            // centre inside the tile: push toward the nearest edge
            const ex = x - (left + TILE / 2);
            const ey = y - (top + TILE / 2);
            if (Math.abs(ex) > Math.abs(ey)) {
              dx = Math.sign(ex) || 1;
              dy = 0;
            } else {
              dx = 0;
              dy = Math.sign(ey) || 1;
            }
            d2 = 1;
          }
          const d = Math.sqrt(d2);
          const push = r - d;
          x += (dx / d) * push;
          y += (dy / d) * push;
        }
      }
    }
    x = Math.max(r, Math.min(this.widthPx - r, x));
    y = Math.max(r, Math.min(this.heightPx - r, y));
    return { x, y };
  }

  /** Finds a walkable point near (x, y) by spiralling outward. */
  nearestWalkable(x: number, y: number, r: number, maxTries = 40): Point {
    if (!this.isSolidAt(x, y) && this.clear(x, y, r)) return { x, y };
    for (let i = 1; i < maxTries; i++) {
      const rad = i * 24;
      for (let k = 0; k < 8; k++) {
        const a = (k / 8) * Math.PI * 2;
        const px = x + Math.cos(a) * rad;
        const py = y + Math.sin(a) * rad;
        if (this.clear(px, py, r)) return { x: px, y: py };
      }
    }
    return this.center;
  }

  clear(x: number, y: number, r: number): boolean {
    if (x < r || y < r || x > this.widthPx - r || y > this.heightPx - r) return false;
    const p = this.resolveCircle(x, y, r);
    return Math.abs(p.x - x) < 0.5 && Math.abs(p.y - y) < 0.5;
  }

  randomPointNear(rng: Rng, x: number, y: number, minD: number, maxD: number, r: number): Point | null {
    for (let i = 0; i < 12; i++) {
      const a = rng.angle();
      const d = rng.float(minD, maxD);
      const px = x + Math.cos(a) * d;
      const py = y + Math.sin(a) * d;
      if (this.clear(px, py, r)) return { x: px, y: py };
    }
    return null;
  }

  static generateRealm(seed: number, size = 160): GameMap {
    const rng = new Rng(seed ^ 0x5ea1);
    const map = new GameMap(size, size, 420, 'realm', seed);
    const c = map.center;
    const maxD = (size * TILE) / 2;
    // tiers by distance from centre
    for (let ty = 0; ty < size; ty++) {
      for (let tx = 0; tx < size; tx++) {
        const d = dist(tx * TILE + TILE / 2, ty * TILE + TILE / 2, c.x, c.y);
        const i = map.idx(tx, ty);
        map.tiers[i] = d < maxD * 0.36 ? 3 : d < maxD * 0.68 ? 2 : 1;
      }
    }
    // border
    for (let i = 0; i < size; i++) {
      map.solid[map.idx(i, 0)] = 1;
      map.solid[map.idx(i, size - 1)] = 1;
      map.solid[map.idx(0, i)] = 1;
      map.solid[map.idx(size - 1, i)] = 1;
      map.solid[map.idx(i, 1)] = 1;
      map.solid[map.idx(i, size - 2)] = 1;
      map.solid[map.idx(1, i)] = 1;
      map.solid[map.idx(size - 2, i)] = 1;
    }
    // obstacle blobs via random walks
    const blobs = Math.floor(size * size * 0.012);
    for (let b = 0; b < blobs; b++) {
      let tx = rng.int(3, size - 4);
      let ty = rng.int(3, size - 4);
      const steps = rng.int(3, 14);
      for (let s = 0; s < steps; s++) {
        const d = dist(tx * TILE, ty * TILE, c.x, c.y);
        if (d > map.arenaRadius + 120) map.solid[map.idx(tx, ty)] = 1;
        const dir = rng.int(0, 3);
        tx += dir === 0 ? 1 : dir === 1 ? -1 : 0;
        ty += dir === 2 ? 1 : dir === 3 ? -1 : 0;
        tx = Math.max(3, Math.min(size - 4, tx));
        ty = Math.max(3, Math.min(size - 4, ty));
      }
    }
    // ruins: small rectangles with gaps
    const ruins = Math.floor(size / 10);
    for (let k = 0; k < ruins; k++) {
      const rw = rng.int(4, 9);
      const rh = rng.int(4, 9);
      const rx = rng.int(4, size - rw - 4);
      const ry = rng.int(4, size - rh - 4);
      const gap = rng.int(0, 3);
      for (let i = 0; i < rw; i++) {
        for (let j = 0; j < rh; j++) {
          const edge = i === 0 || j === 0 || i === rw - 1 || j === rh - 1;
          if (!edge) continue;
          if ((gap === 0 && j === 0 && i === Math.floor(rw / 2)) || (gap === 1 && i === 0 && j === Math.floor(rh / 2)) || (gap === 2 && j === rh - 1 && i === Math.floor(rw / 2)) || (gap === 3 && i === rw - 1 && j === Math.floor(rh / 2))) continue;
          const d = dist((rx + i) * TILE, (ry + j) * TILE, c.x, c.y);
          if (d > map.arenaRadius + 160) map.solid[map.idx(rx + i, ry + j)] = 1;
        }
      }
    }
    // corridors from edge to centre guarantee connectivity
    const corridors = 8;
    for (let k = 0; k < corridors; k++) {
      const a = (k / corridors) * Math.PI * 2 + rng.float(-0.15, 0.15);
      const steps = Math.floor(maxD / (TILE / 2));
      for (let s = 0; s < steps; s++) {
        const px = c.x + Math.cos(a) * s * (TILE / 2);
        const py = c.y + Math.sin(a) * s * (TILE / 2);
        const tx = Math.floor(px / TILE);
        const ty = Math.floor(py / TILE);
        for (let dx = -1; dx <= 1; dx++) {
          for (let dy = -1; dy <= 1; dy++) {
            const ux = tx + dx;
            const uy = ty + dy;
            if (ux >= 2 && uy >= 2 && ux < size - 2 && uy < size - 2) map.solid[map.idx(ux, uy)] = 0;
          }
        }
      }
      const spawnD = maxD * 0.9;
      const sx = c.x + Math.cos(a) * spawnD;
      const sy = c.y + Math.sin(a) * spawnD;
      map.spawnPoints.push(map.nearestWalkable(sx, sy, 20));
    }
    // clear arena
    const ar = Math.ceil((map.arenaRadius + 40) / TILE);
    const ctx = Math.floor(c.x / TILE);
    const cty = Math.floor(c.y / TILE);
    for (let dx = -ar; dx <= ar; dx++) {
      for (let dy = -ar; dy <= ar; dy++) {
        if (dx * dx + dy * dy <= ar * ar) map.solid[map.idx(ctx + dx, cty + dy)] = 0;
      }
    }
    return map;
  }

  static generateArena(seed: number): GameMap {
    const size = 40;
    const rng = new Rng(seed ^ 0xa7e4a);
    const map = new GameMap(size, size, 0, 'arena', seed);
    for (let i = 0; i < size; i++) {
      map.solid[map.idx(i, 0)] = 1;
      map.solid[map.idx(i, size - 1)] = 1;
      map.solid[map.idx(0, i)] = 1;
      map.solid[map.idx(size - 1, i)] = 1;
    }
    // symmetric pillars
    const pillars = rng.int(4, 7);
    for (let k = 0; k < pillars; k++) {
      const px = rng.int(6, size / 2 - 3);
      const py = rng.int(6, size - 7);
      const w = rng.int(1, 3);
      const h = rng.int(1, 3);
      for (let i = 0; i < w; i++) {
        for (let j = 0; j < h; j++) {
          map.solid[map.idx(px + i, py + j)] = 1;
          map.solid[map.idx(size - 1 - (px + i), py + j)] = 1;
        }
      }
    }
    map.spawnPoints.push(map.nearestWalkable(6 * TILE, (size / 2) * TILE, 20));
    map.spawnPoints.push(map.nearestWalkable((size - 6) * TILE, (size / 2) * TILE, 20));
    for (let i = 0; i < size * size; i++) map.tiers[i] = 2;
    return map;
  }

  /** Compact description for the client so it can rebuild the same map deterministically. */
  descriptor(): { kind: 'realm' | 'arena'; seed: number; w: number; h: number } {
    return { kind: this.kind, seed: this.seed, w: this.w, h: this.h };
  }

  static fromDescriptor(d: { kind: 'realm' | 'arena'; seed: number; w: number; h: number }): GameMap {
    return d.kind === 'realm' ? GameMap.generateRealm(d.seed, d.w) : GameMap.generateArena(d.seed);
  }
}
